// ── Invite Accept Page ──
//
// Public landing page for `#/invite/:token` — must render for a logged-out
// visitor, so it follows login.js's pattern (paints straight into #app,
// calls teardownLayout()), not settings.js's (which redirects via
// checkAuth()). Logged-out visitors are bounced through /login and the
// pending token is replayed after sign-in (see index.js) (C1168).

import { apiFetch, api } from '../api.js';
import { verifySession } from '../auth.js';
import { navigate } from '../router.js';
import { teardownLayout } from '../layout.js';
import { showToast, esc } from '../ui.js';
import { brandLockup } from '../lib/brand.js';

const PENDING_INVITE_KEY = 'pending_invite';

function renderShell(bodyHTML) {
  const app = document.getElementById('app');
  app.innerHTML = `<div class="login-container">${brandLockup()}<div class="auth-card">${bodyHTML}</div></div>`;
  return app;
}

export async function invitePage(params) {
  teardownLayout();
  const token = params.token;

  let invite;
  try {
    const res = await apiFetch('/invites/' + encodeURIComponent(token), { _silent: true });
    invite = res.invite;
  } catch (err) {
    if (err.status === 410 || err.body?.expired) {
      renderShell(`<p>This invitation has expired — ask the project owner to resend it.</p>`);
    } else {
      renderShell(`<p>This invitation link is not valid.</p>`);
    }
    return;
  }

  const app = renderShell(`
    <p><strong>${esc(invite.inviter_email || 'Someone')}</strong> invited you to collaborate on
       <strong>${esc(invite.project_name)}</strong> as an <strong>${esc(invite.role)}</strong>.</p>
    <div class="login-error" id="invite-error"></div>
    <button type="button" class="btn btn-primary" id="invite-action" style="width:100%;">Loading…</button>`);

  const btn = app.querySelector('#invite-action');
  const errEl = app.querySelector('#invite-error');

  const loggedIn = await verifySession();

  if (!loggedIn) {
    btn.textContent = 'Sign in to accept';
    btn.addEventListener('click', () => {
      sessionStorage.setItem(PENDING_INVITE_KEY, token);
      navigate('/login');
    });
    return;
  }

  btn.textContent = 'Accept invitation';
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    btn.textContent = 'Joining…';
    try {
      const res = await api.post('/invites/' + encodeURIComponent(token) + '/accept');
      showToast('Joined ' + res.project.name, 'success');
      navigate('/projects/' + res.project.id + '/board');
    } catch (err) {
      errEl.textContent = err.body?.error || 'Could not accept invitation';
      btn.disabled = false;
      btn.textContent = 'Accept invitation';
    }
  });
}

// Called once at boot (index.js) — if a token was stashed before bouncing
// through /login, replay it now that the session is live.
export function consumePendingInvite() {
  const token = sessionStorage.getItem(PENDING_INVITE_KEY);
  if (!token) return false;
  sessionStorage.removeItem(PENDING_INVITE_KEY);
  window.location.hash = '#/invite/' + token;
  return true;
}
